import { useEffect, useMemo, useState } from 'react'
import { AuthService, CommonConstant, CustomerService, ErrorConstant } from 'utility'

/** Company 34930 uses GET /user/CustomClients for notification-group customer scope. */
export const CUSTOM_CLIENTS_COMPANY_ID = 34930

const mapCustomClientRow = item => ({
    unique_id: item.UniqueId ?? item.unique_id,
    customer_name: item.DisplayText ?? item.customer_name,
    DisplayText: item.DisplayText ?? item.customer_name,
    UniqueId: item.UniqueId ?? item.unique_id,
})

const normalizeCustomClientItems = result => {
    const items = Array.isArray(result)
        ? result
        : result?.Items || result?.Data || result?.data || []

    if (!Array.isArray(items)) {
        return []
    }

    return items
        .map(mapCustomClientRow)
        .filter(item => item.unique_id != null && item.customer_name)
        .sort((a, b) =>
            String(a.customer_name).localeCompare(String(b.customer_name), undefined, {
                sensitivity: 'base',
            }),
        )
}

/**
 * @param {number} companyId
 * @param {{ forceStandardList?: boolean }} [options] Pass forceStandardList to always
 *        use the standard customer API (used by the Group notification modal).
 */
export const GetNotificationGroupCustomerList = (companyId, options = {}) => {
    const { forceStandardList = false } = options
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const useCustomClients =
        !forceStandardList && Number(companyId) === CUSTOM_CLIENTS_COMPANY_ID

    const options_ = useMemo(
        () =>
            data.map(ele => ({
                value: ele.unique_id ?? ele.UniqueId,
                text: ele.customer_name ?? ele.DisplayText,
            })),
        [data],
    )

    useEffect(() => {
        let isMounted = true

        const fetchData = async () => {
            if (!companyId) {
                if (isMounted) {
                    setData([])
                }
                return
            }

            try {
                setLoading(true)
                if (useCustomClients) {
                    const result = await CustomerService.getCustomClients(
                        companyId,
                    )
                    if (isMounted) {
                        setData(normalizeCustomClientItems(result))
                    }
                } else {
                    const result = await CustomerService.list({
                        PageNumber: 1,
                        PageSize: 5000000,
                    })
                    if (isMounted) {
                        setData(result.Items || [])
                    }
                }
            } catch {
                if (isMounted) {
                    setError(ErrorConstant.default)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        fetchData()

        return () => {
            isMounted = false
        }
    }, [companyId, useCustomClients])

    return { data, options: options_, error, loading }
}

export const GetCustomerList = (extraQueryParams = {}) => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: CommonConstant.defaultPageSize,
        unique_id: undefined,
        ...extraQueryParams,
    })
    const options = useMemo(() => data.map(ele => ({
        value: ele.unique_id,
        text: ele.customer_name,
    })), [data])

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await CustomerService.list(filter)
                setData(result.Items)
                setTotalCount(result.TotalCount)
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }

        fetchData()

    }, [filter])

    const pageChanged = (page) => {
        setFilter({
            ...filter,
            PageNumber: page,
        })
    }

    const refresh = () => {
        setFilter({
            ...filter,
        })
    }

    return { data, options, setData, error, loading, filter, totalCount, pageChanged, refresh }
}

// Cache for customer groups to prevent multiple API calls
const customerGroupsCache = new Map()

export const useCustomerGroups = (companyId, refreshKey = 0) => {
    const [groups, setGroups] = useState([])

    useEffect(() => {
        if (!companyId) return

        const cacheKey = `${companyId}-${refreshKey}`

        // Check if we have cached data
        if (customerGroupsCache.has(cacheKey)) {
            setGroups(customerGroupsCache.get(cacheKey))
            return
        }

        // Fetch new data
        CustomerService.getCustomerGroups(companyId).then(data => {
            setGroups(data)
            // Cache the result
            customerGroupsCache.set(cacheKey, data)
        })
    }, [companyId, refreshKey])

    return groups
}

export const useCustomerAdditionalInfo = (customerId) => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const fetchData = async () => {
        if (!customerId) return
        setLoading(true)
        setError('')
        try {
            const result = await CustomerService.getCustomerAdditionalInfo(customerId)
            setData(result)
        } catch (e) {
            setError(ErrorConstant.default)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [customerId])

    return { data, loading, error, refresh: fetchData }
}

export const GetExternalCustomerList = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const options = useMemo(() => data.map(ele => ({
        value: ele.UniqueId,
        text: ele.CustomerName,
    })), [data])

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await CustomerService.externalCustomerList({ userId: AuthService.getUser().unique_id, companyId: AuthService.getUser()?.Companies?.[0]?.CompanyId })
                setData(result)
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }

        fetchData()

    }, [])

    return { data, options, setData, error, loading }
}